import {
  Component,
  computed,
  Inject,
  Input,
  signal,
  WritableSignal,
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { LoggedUser, PtConsoleRoutes, PtUiAppRoutes } from '@pt/pt-models';
import { Router } from '@angular/router';
import { SecretGenerateResponse } from '@primatoo-ms-organization';
import { PtCoreActions, PtCoreSelectors } from '../../store';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdmUsersService, ApiClientConfig, UsersService } from '@pt/api-client';
import { UrlUtils } from '../../utils';

type DialogMode = 'RESET' | 'GENERATE' | 'NOTIFICATION';

@Component({
  selector: 'pt-two-factor-authentication-dialog',
  template: `
    <ng-container [ngSwitch]="dialogMode">
      <ng-container *ngSwitchCase="'RESET'">
        <pt-two-factor-authentication-reset
          class="display-contents"
          (pressCancel)="activeModal.close(false)"
          (pressConfirm)="activeModal.close(true)"
        >
        </pt-two-factor-authentication-reset>
      </ng-container>

      <ng-container *ngSwitchCase="'GENERATE'">
        <pt-two-factor-authentication-generate
          class="display-contents"
          [qrCodeLink]="secretGenerated?.url"
          [forceConfiguration]="forceConfiguration()"
          (cancelClicked)="activeModal.close(false)"
          (confirmClicked)="confirmGeneration($event)"
          (logout)="logout()"
        >
        </pt-two-factor-authentication-generate>
      </ng-container>

      <ng-container *ngSwitchCase="'NOTIFICATION'">
        <pt-two-factor-authentication-notification
          class="display-contents"
          [forceConfiguration]="forceConfiguration()"
          (pressConfigure)="configure()"
          (pressProfile)="openProfile()"
          (pressCancel)="activeModal.close(false)"
          (logout)="logout()"
        >
        </pt-two-factor-authentication-notification>
      </ng-container>
    </ng-container>
  `,
})
export class TwoFactorAuthenticationDialogComponent {
  @Input() dialogMode: DialogMode = 'NOTIFICATION';
  @Input() secretGenerated: SecretGenerateResponse;
  profileLink!: string;
  public loginResponse$ = this.store.select(PtCoreSelectors.loginResponse);
  public loggedUser: WritableSignal<LoggedUser | null> = signal(null);

  private isConsole = false;

  public forceConfiguration = computed(() => {
    const lu = this.loggedUser();
    if (lu) {
      return lu.twoFactorAuthEnabled && !lu.hasOtpSecret;
    }
    return false;
  });

  constructor(
    public activeModal: NgbActiveModal,
    @Inject(ApiClientConfig) private config: ApiClientConfig,

    private store: Store,
    private userService: UsersService,
    private admUserService: AdmUsersService,
    private router: Router
  ) {
    const host = `${window.location.protocol}//${window.location.host}`;
    this.profileLink = `${host}/${PtUiAppRoutes.PROFILE}`;

    this.loginResponse$.pipe(takeUntilDestroyed()).subscribe((loggedUser) => {
      this.loggedUser.set(loggedUser);
    });
    this.isConsole = this.config.env.appId === 'primatoo-ui-console';
  }

  openProfile() {
    this.router.navigate([PtConsoleRoutes.PROFILE]);
    this.activeModal.close(false);
  }

  configure() {
    if (this.forceConfiguration()) {
      if (this.isConsole) {
        this.admUserService.selfOtpGenerate().subscribe((success) => {
          this.secretGenerated = success;
          this.dialogMode = 'GENERATE';
        });
      } else {
        this.userService.selfOtpGenerate().subscribe((success) => {
          this.secretGenerated = success;
          this.dialogMode = 'GENERATE';
        });
      }
    }
  }

  protected logout() {
    this.store.dispatch(
      PtCoreActions.logoutAndClearData({
        route: [PtConsoleRoutes.LOGIN],
        domain: UrlUtils.getDomainIfExists(),
        // queryParams: { err: 'oauth' },
      })
    );
  }

  protected confirmGeneration(response: {
    qrCodeLink: string;
    otpCode: string;
  }) {
    const secret = this.secretGenerated?.secret;
    const otpCode = response.otpCode;

    if (this.forceConfiguration() && secret) {
      if (this.isConsole) {
        this.admUserService.selfOtpUpdate({ secret, otpCode }).subscribe(() => {
          this.logout();
        });
      } else {
        this.userService.selfOtpUpdate({ secret, otpCode }).subscribe(() => {
          this.logout();
        });
      }
    } else {
      this.activeModal.close({ secret, otpCode });
    }
  }
}
